import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { collection, query, where, getDocs, onSnapshot, orderBy, limit, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useCurrency } from '@/contexts/CurrencyContext';
import { useToast } from '@/hooks/useToast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Edit,
  Trash2,
  Radio,
  Globe,
  ChevronUp,
  ChevronDown,
  QrCode,
  Download,
  ExternalLink,
  DollarSign,
  TrendingUp,
  Wallet,
  CreditCard,
  Calendar,
  History,
} from '@/features/admin/components/AdminModalIcons';
import QRCode from 'qrcode.react';
import { buildArtistPublicUrl } from '@/lib/urlHelpers';
import { downloadQrPngHighRes, getQrPngDataUrlHighRes, QR_HIGH_RES_PX } from '@/lib/qrHighRes';
import { COUNTRIES, getCountryName } from '@/lib/countries';
import { getCurrencyForCountry } from '@/lib/currency';
import { formatUkDateTime, formatUkDate, formatUkTime } from '@/lib/adminFormat';
import { applyAdminLedgerDisplay } from '@/lib/payoutLedger';
import {
  profileHasStripeLegacyData,
  buildStripeLegacyProfilePurgeUpdate,
  hasManualPayoutDetailsOnProfile,
} from '@/lib/stripeProfilePurge';

export const ArtistDetailsModal = ({
  artist,
  liveStats,
  onClose,
  onUpdate,
  onDelete,
  onForceOffline,
  onStripeLegacyRemoved,
}) => {
  const { format } = useCurrency();
  const [editing, setEditing] = useState(false);
  const [sessionHistory, setSessionHistory] = useState([]);
  const [loadingSessionHistory, setLoadingSessionHistory] = useState(false);
  const [showSocialMedia, setShowSocialMedia] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [showFinancial, setShowFinancial] = useState(false);
  const [financialData, setFinancialData] = useState({
    totalEarnings: 0,
    afterCommission: 0,
    currentBalance: 0,
    payoutHistory: [],
    manualPayoutOnFile: false,
  });
  const [loadingFinancial, setLoadingFinancial] = useState(false);
  const [showSessionHistory, setShowSessionHistory] = useState(false);
  const qrCodeRef = useRef(null);
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    artist_name: artist.artist_name || '',
    email: artist.email || '',
    bio: artist.bio || '',
    url_slug: artist.url_slug || '',
    social_facebook: artist.social_facebook || '',
    social_instagram: artist.social_instagram || '',
    social_tiktok: artist.social_tiktok || '',
    social_x: artist.social_x || '',
    full_name: artist.full_name || artist.legalName || '',
    date_of_birth: artist.date_of_birth || artist.dob || '',
    country: (artist.country || 'GB').toString().toUpperCase().slice(0, 2),
  });

  useEffect(() => {
    if (!artist) return;
    setFormData({
      artist_name: artist.artist_name || '',
      email: artist.email || '',
      bio: artist.bio || '',
      url_slug: artist.url_slug || '',
      social_facebook: artist.social_facebook || '',
      social_instagram: artist.social_instagram || '',
      social_tiktok: artist.social_tiktok || '',
      social_x: artist.social_x || '',
      full_name: artist.full_name || artist.legalName || '',
      date_of_birth: artist.date_of_birth || artist.dob || '',
      country: (artist.country || 'GB').toString().toUpperCase().slice(0, 2),
    });
  }, [artist]);

  const stripePurgeHandledForIdRef = useRef(null);

  useEffect(() => {
    stripePurgeHandledForIdRef.current = null;
  }, [artist?.id]);

  // Opening an artist in admin: permanently remove legacy Stripe Connect fields from Firestore.
  useEffect(() => {
    if (!artist?.id) return;
    if (stripePurgeHandledForIdRef.current === artist.id) return;

    if (!profileHasStripeLegacyData(artist)) {
      stripePurgeHandledForIdRef.current = artist.id;
      return;
    }

    stripePurgeHandledForIdRef.current = artist.id;
    let cancelled = false;

    (async () => {
      try {
        const ref = doc(db, 'profiles', artist.id);
        await updateDoc(ref, {
          ...buildStripeLegacyProfilePurgeUpdate(),
          updated_at: new Date().toISOString(),
        });
        if (cancelled) return;
        onStripeLegacyRemoved?.(artist.id);
      } catch (err) {
        console.error('Stripe legacy purge failed:', err);
        stripePurgeHandledForIdRef.current = null;
        if (!cancelled) {
          toast({
            variant: 'destructive',
            title: 'Could not clear Stripe fields',
            description: err?.message || 'Check permissions and try again.',
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [artist, toast, onStripeLegacyRemoved]);

  // Load financial data when financial section is shown (no Stripe APIs)
  useEffect(() => {
    if (showFinancial && artist?.id) {
      setLoadingFinancial(true);
      const loadFinancialData = async () => {
        try {
          const requestsQuery = query(
            collection(db, 'requests'),
            where('artist_id', '==', artist.id)
          );
          const requestsSnapshot = await getDocs(requestsQuery);

          let totalEarnings = 0;
          requestsSnapshot.docs.forEach((d) => {
            const data = d.data();
            if (data.amount_paid > 0 && data.payment_intent_id) {
              totalEarnings += data.amount_paid || 0;
            }
          });

          const afterCommission = totalEarnings * 0.7;
          const display = applyAdminLedgerDisplay({
            gross: totalEarnings,
            artistShare: afterCommission,
            paidOut: 0,
            reserved: 0,
          }, artist);

          setFinancialData({
            totalEarnings: display.gross,
            afterCommission: display.artistShare,
            currentBalance: display.currentBalance,
            payoutHistory: [],
            manualPayoutOnFile: hasManualPayoutDetailsOnProfile(artist),
          });
        } catch (error) {
          console.error('Error loading financial data:', error);
        } finally {
          setLoadingFinancial(false);
        }
      };

      loadFinancialData();
    }
  }, [showFinancial, artist]);

  // Real-time session history listener - ALWAYS load when artist modal is open
  useEffect(() => {
    if (artist?.id) {
      setLoadingSessionHistory(true);
      let unsubscribe = null;
      
      try {
        const historyQuery = query(
          collection(db, 'session_history'),
          where('user_id', '==', artist.id),
          orderBy('end_time', 'desc'),
          limit(50) // Increased limit to show more sessions
        );
        
        // Real-time listener for session history
        unsubscribe = onSnapshot(historyQuery, (snapshot) => {
          const sessions = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              start_time: data.start_time?.toDate ? data.start_time.toDate() : new Date(data.start_time),
              end_time: data.end_time?.toDate ? data.end_time.toDate() : new Date(data.end_time),
              // Use actual viewer_count and peak_viewers from session_history (real data, not estimated)
              viewer_count: data.viewer_count || data.peak_viewers || 0,
              peak_viewers: data.peak_viewers || data.viewer_count || 0
            };
          });
          
          setSessionHistory(sessions);
          setLoadingSessionHistory(false);
        }, (error) => {
          console.error('Error listening to session history:', error);
          // Fallback: try without orderBy if index missing
          if (error.code === 'failed-precondition') {
            try {
              const fallbackQuery = query(
                collection(db, 'session_history'),
                where('user_id', '==', artist.id)
              );
              unsubscribe = onSnapshot(fallbackQuery, (snapshot) => {
                const sessions = snapshot.docs
                  .map(doc => {
                    const data = doc.data();
                    return {
                      id: doc.id,
                      ...data,
                      start_time: data.start_time?.toDate ? data.start_time.toDate() : new Date(data.start_time),
                      end_time: data.end_time?.toDate ? data.end_time.toDate() : new Date(data.end_time),
                      viewer_count: data.viewer_count || data.peak_viewers || 0,
                      peak_viewers: data.peak_viewers || data.viewer_count || 0
                    };
                  })
                  .sort((a, b) => {
                    const ta = a.end_time?.getTime?.() ?? 0;
                    const tb = b.end_time?.getTime?.() ?? 0;
                    return tb - ta;
                  })
                  .slice(0, 50);
                setSessionHistory(sessions);
                setLoadingSessionHistory(false);
              });
            } catch (fallbackError) {
              console.error('Error with fallback query:', fallbackError);
              setSessionHistory([]);
              setLoadingSessionHistory(false);
            }
          } else {
            setSessionHistory([]);
            setLoadingSessionHistory(false);
          }
        });
      } catch (error) {
        console.error('Error setting up session history listener:', error);
        setSessionHistory([]);
        setLoadingSessionHistory(false);
      }
      
      return () => {
        if (unsubscribe) unsubscribe();
      };
    } else {
      setSessionHistory([]);
      setLoadingSessionHistory(false);
    }
  }, [artist?.id]); // Always load when artist modal opens, not just when viewing live stats

  const downloadQRCode = async () => {
    const url = buildArtistPublicUrl(artist.url_slug);
    if (!url) {
      toast({ title: 'No URL', description: 'Artist needs a URL slug for the QR code.', variant: 'destructive' });
      return;
    }
    try {
      await downloadQrPngHighRes(url, `qr-${artist.url_slug || artist.id}`);
      toast({
        title: 'Downloaded',
        description: `High-resolution PNG (${QR_HIGH_RES_PX}×${QR_HIGH_RES_PX}px, print-ready).`,
      });
    } catch (e) {
      console.error(e);
      toast({ title: 'Download failed', description: e.message, variant: 'destructive' });
    }
  };

  const openQRInNewTab = async () => {
    const url = buildArtistPublicUrl(artist.url_slug);
    if (!url) {
      toast({ title: 'No URL', description: 'Artist needs a URL slug for the QR code.', variant: 'destructive' });
      return;
    }
    try {
      const dataUrl = await getQrPngDataUrlHighRes(url);
      const newWindow = window.open();
      if (newWindow) {
        const safeName = String(artist.artist_name || 'Artist').replace(/</g, '');
        newWindow.document.write(`
        <html>
          <head><title>QR Code - ${safeName}</title></head>
          <body style="margin:0;padding:20px;display:flex;justify-content:center;align-items:center;background:#f0f0f0;">
            <div style="text-align:center;max-width:min(100%,${QR_HIGH_RES_PX}px);">
              <img src="${dataUrl}" alt="QR Code" style="width:100%;height:auto;display:block;" />
              <p style="margin-top:20px;font-family:system-ui,sans-serif;color:#333;">High-resolution preview — use Download for the PNG file.</p>
            </div>
          </body>
        </html>
      `);
      }
    } catch (e) {
      console.error(e);
      toast({ title: 'Could not open preview', description: e.message, variant: 'destructive' });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    const cc = (formData.country || 'GB').toString().toUpperCase().slice(0, 2);
    onUpdate(artist.id, {
      ...formData,
      country: cc,
      currency: getCurrencyForCountry(cc),
    });
    setEditing(false);
  };

  const handleCancel = () => {
    setFormData({
      artist_name: artist.artist_name || '',
      email: artist.email || '',
      bio: artist.bio || '',
      url_slug: artist.url_slug || '',
      social_facebook: artist.social_facebook || '',
      social_instagram: artist.social_instagram || '',
      social_tiktok: artist.social_tiktok || '',
      social_x: artist.social_x || '',
      full_name: artist.full_name || artist.legalName || '',
      date_of_birth: artist.date_of_birth || artist.dob || '',
      country: (artist.country || 'GB').toString().toUpperCase().slice(0, 2),
    });
    setEditing(false);
  };

  const getSessionTipTotal = (session) => {
    const stored = Number(session.total_tips) || 0;
    const fromRequests = (session.requests || []).reduce((sum, req) => {
      const tip = Number(req.tip) || 0;
      if (tip > 0) return sum + tip;
      if (req.is_tip_only) return sum + (Number(req.amount_paid) || 0);
      return sum;
    }, 0);
    return Math.max(stored, fromRequests);
  };

  const handleDeleteSession = async (sessionId) => {
    if (!sessionId) return;
    if (!window.confirm('Delete this session from history? This cannot be undone.')) return;
    try {
      await deleteDoc(doc(db, 'session_history', sessionId));
      setSessionHistory((prev) => prev.filter((s) => s.id !== sessionId));
      toast({
        title: 'Session deleted',
        description: 'The session was removed from history.',
      });
    } catch (error) {
      console.error('Error deleting session:', error);
      toast({
        title: 'Delete failed',
        description: error.message || 'Could not delete this session.',
        variant: 'destructive',
      });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-white">Artist Details</h2>
            <div className="flex space-x-2">
              {editing ? (
                <>
                  <Button size="sm" onClick={handleCancel} variant="outline">
                    Cancel
                  </Button>
                  <Button size="sm" onClick={handleSave} className="bg-green-600 hover:bg-green-700">
                    Save
                  </Button>
                </>
              ) : (
                <>
                  {liveStats?.isCurrentlyLive && (
                    <Button 
                      size="sm" 
                      onClick={() => onForceOffline && onForceOffline(artist)} 
                      variant="outline" 
                      className="border-orange-500 text-orange-400 hover:bg-orange-500 hover:text-white"
                    >
                      <Radio className="w-4 h-4 mr-1" />
                      Force Offline
                    </Button>
                  )}
                  <Button size="sm" onClick={() => setEditing(true)} variant="outline">
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button size="sm" onClick={() => onDelete(artist.id)} variant="outline" className="border-red-500 text-red-400 hover:bg-red-500 hover:text-white">
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </>
              )}
              <Button size="sm" onClick={onClose} variant="outline">
                Close
              </Button>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Artist Name</label>
              {editing ? (
                <Input
                  name="artist_name"
                  value={formData.artist_name}
                  onChange={handleInputChange}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              ) : (
                <p className="text-white">{artist.artist_name || 'Not set'}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
              {editing ? (
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              ) : (
                <p className="text-white">{artist.email || 'Not set'}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">URL Slug</label>
              {editing ? (
                <Input
                  name="url_slug"
                  value={formData.url_slug}
                  onChange={handleInputChange}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              ) : (
                <p className="text-white">{artist.url_slug || 'Not set'}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Account Type</label>
              <Badge variant="outline" className="border-blue-500 text-blue-400">
                {artist.account_type || 'artist'}
              </Badge>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Country / Region</label>
              {editing ? (
                <>
                  <select
                    name="country"
                    value={formData.country || 'GB'}
                    onChange={handleInputChange}
                    className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.label}
                    </option>
                  ))}
                  </select>
                  <p className="text-xs text-gray-400 mt-1">
                    Updates YSR pricing currency to {getCurrencyForCountry((formData.country || 'GB').toString().toUpperCase().slice(0, 2))}.
                  </p>
                </>
              ) : (
                <p className="text-white">
                  {getCountryName(artist.country) || artist.country || 'Not set'} ({artist.currency || '—'})
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Legal Full Name</label>
              {editing ? (
                <Input
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  placeholder="Legal full name"
                  className="bg-gray-700 border-gray-600 text-white"
                />
              ) : (
                <p className="text-white">{artist.full_name || artist.legalName || 'Not set'}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Date of Birth (DD/MM/YYYY)</label>
              {editing ? (
                <Input
                  name="date_of_birth"
                  value={formData.date_of_birth}
                  onChange={handleInputChange}
                  placeholder="05/07/1995"
                  className="bg-gray-700 border-gray-600 text-white"
                />
              ) : (
                <p className="text-white">{artist.date_of_birth || artist.dob || 'Not set'}</p>
              )}
              {editing && (
                <p className="text-xs text-gray-400 mt-1">Format: DD/MM/YYYY (e.g., 05/07/1995 for 5th July 1995)</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Bio</label>
            {editing ? (
              <textarea
                name="bio"
                value={formData.bio}
                onChange={handleInputChange}
                rows={4}
                className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-white"
              />
            ) : (
              <p className="text-white">{artist.bio || 'No bio provided'}</p>
            )}
          </div>

          {/* Social Media - Collapsible */}
          <div className="border border-gray-700 rounded-lg">
            <button
              onClick={() => setShowSocialMedia(!showSocialMedia)}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-700/50 transition-colors"
            >
              <h3 className="text-sm font-semibold text-white flex items-center">
                <Globe className="w-4 h-4 mr-2" />
                Social Media
              </h3>
              {showSocialMedia ? (
                <ChevronUp className="w-4 h-4 text-gray-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-400" />
              )}
            </button>
            {showSocialMedia && (
              <div className="p-4 pt-0 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Facebook</label>
                  {editing ? (
                    <Input
                      name="social_facebook"
                      value={formData.social_facebook}
                      onChange={handleInputChange}
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  ) : (
                    <p className="text-white">{artist.social_facebook || 'Not set'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Instagram</label>
                  {editing ? (
                    <Input
                      name="social_instagram"
                      value={formData.social_instagram}
                      onChange={handleInputChange}
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  ) : (
                    <p className="text-white">{artist.social_instagram || 'Not set'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">TikTok</label>
                  {editing ? (
                    <Input
                      name="social_tiktok"
                      value={formData.social_tiktok}
                      onChange={handleInputChange}
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  ) : (
                    <p className="text-white">{artist.social_tiktok || 'Not set'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">X (Twitter)</label>
                  {editing ? (
                    <Input
                      name="social_x"
                      value={formData.social_x}
                      onChange={handleInputChange}
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  ) : (
                    <p className="text-white">{artist.social_x || 'Not set'}</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* QR Code Section - Collapsed by default */}
          {artist.url_slug && (
            <div className="pt-4 border-t border-gray-700">
              <button
                onClick={() => setShowQRCode(!showQRCode)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-700/50 transition-colors rounded-lg"
              >
                <h3 className="text-lg font-semibold text-white flex items-center">
                  <QrCode className="w-5 h-5 mr-2" />
                  QR Code
                </h3>
                {showQRCode ? (
                  <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </button>
              {showQRCode && (
                <div className="flex flex-col items-center space-y-4 pt-4">
                  <div className="p-4 bg-white rounded-lg inline-block" ref={qrCodeRef}>
                    <QRCode
                      value={buildArtistPublicUrl(artist.url_slug) || ''}
                      size={200}
                      level="H"
                      includeMargin={true}
                    />
                  </div>
                  <div className="text-center space-y-2">
                    <p className="text-sm text-gray-400">
                      {buildArtistPublicUrl(artist.url_slug) || 'URL unavailable'}
                    </p>
                    <div className="flex space-x-2 justify-center">
                      <Button
                        size="sm"
                        onClick={downloadQRCode}
                        variant="outline"
                        className="border-green-500 text-green-400 hover:bg-green-500 hover:text-white"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Download HD PNG
                      </Button>
                      <Button
                        size="sm"
                        onClick={openQRInNewTab}
                        variant="outline"
                        className="border-blue-500 text-blue-400 hover:bg-blue-500 hover:text-white"
                      >
                        <ExternalLink className="w-4 h-4 mr-1" />
                        Open in New Tab
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Financial Section - Collapsible */}
          <div className="pt-4 border-t border-gray-700">
            <button
              onClick={() => setShowFinancial(!showFinancial)}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-700/50 transition-colors rounded-lg"
            >
              <h3 className="text-lg font-semibold text-white flex items-center">
                <DollarSign className="w-5 h-5 mr-2" />
                Financial Information
              </h3>
              {showFinancial ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>
            {showFinancial && (
              <div className="p-4 pt-0 space-y-4">
                {loadingFinancial ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500 mx-auto mb-2"></div>
                    <p className="text-gray-400">Loading financial data...</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-gradient-to-br from-green-600/20 to-green-800/20 border border-green-500/40 rounded-lg p-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <DollarSign className="w-5 h-5 text-green-400" />
                        <label className="text-sm font-medium text-gray-300">Total Made (All-Time)</label>
                      </div>
                      <p className="text-2xl font-bold text-white">{format(financialData.totalEarnings)}</p>
                      <p className="text-xs text-gray-400 mt-1">From paid requests only</p>
                    </div>
                    
                    <div className="bg-gradient-to-br from-blue-600/20 to-blue-800/20 border border-blue-500/40 rounded-lg p-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <TrendingUp className="w-5 h-5 text-blue-400" />
                        <label className="text-sm font-medium text-gray-300">After Commission (70%)</label>
                      </div>
                      <p className="text-2xl font-bold text-white">{format(financialData.afterCommission)}</p>
                      <p className="text-xs text-gray-400 mt-1">Artist's share</p>
                    </div>
                    
                    <div className="bg-gradient-to-br from-purple-600/20 to-purple-800/20 border border-purple-500/40 rounded-lg p-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <Wallet className="w-5 h-5 text-purple-400" />
                        <label className="text-sm font-medium text-gray-300">Current Balance</label>
                      </div>
                      <p className="text-2xl font-bold text-white">{format(financialData.currentBalance)}</p>
                      <p className="text-xs text-gray-400 mt-1">Estimated share from paid requests (YSR manual payouts)</p>
                    </div>
                    
                    <div className="bg-gray-700 rounded-lg p-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <CreditCard className="w-5 h-5 text-gray-400" />
                        <label className="text-sm font-medium text-gray-300">Manual payout details</label>
                      </div>
                      <p className="text-lg font-semibold text-white">
                        {financialData.manualPayoutOnFile ? (
                          <span className="text-green-400">On file</span>
                        ) : (
                          <span className="text-orange-400">Missing</span>
                        )}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {(artist.country || 'GB').toString().toUpperCase().slice(0, 2) === 'US'
                          ? 'US artists: PayPal email and/or Venmo handle in profile.'
                          : 'UK artists: sort code + account number in profile.'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Session History Section - Collapsible - ALWAYS VISIBLE */}
          <div className="pt-4 border-t border-gray-700">
            <button
              onClick={() => setShowSessionHistory(!showSessionHistory)}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-700/50 transition-colors rounded-lg"
            >
              <h3 className="text-lg font-semibold text-white flex items-center">
                <Calendar className="w-5 h-5 mr-2" />
                Session History
                {sessionHistory.length > 0 && (
                  <Badge variant="outline" className="ml-2 border-blue-500 text-blue-400">
                    {sessionHistory.length}
                  </Badge>
                )}
              </h3>
              {showSessionHistory ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>
            {showSessionHistory && (
              <div className="p-4 pt-0 space-y-4">
                {loadingSessionHistory ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                    <p className="text-gray-400">Loading session history...</p>
                  </div>
                ) : sessionHistory.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No session history available</p>
                    <p className="text-sm text-gray-500 mt-2">Sessions will appear here after they end</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {sessionHistory.map((session) => {
                      const isKaraokeSession = String(session.session_id || '').startsWith('karaoke_');
                      const totalTips = getSessionTipTotal(session);
                      return (
                      <div key={session.id} className="bg-gray-700 rounded-lg p-4 hover:bg-gray-600 transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <div className="flex items-center space-x-2">
                                <Calendar className="w-4 h-4 text-gray-400" />
                                <span className="text-sm font-medium text-white">
                                  {formatUkDate(session.end_time)}
                                </span>
                              </div>
                              <span className="text-xs text-gray-400">
                                {formatUkTime(session.end_time)}
                              </span>
                            </div>
                            
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                              <div>
                                <p className="text-xs text-gray-400">Viewers</p>
                                <p className="text-lg font-bold text-blue-400">{session.viewer_count || session.peak_viewers || 0}</p>
                                {session.peak_viewers > (session.viewer_count || 0) && (
                                  <p className="text-xs text-gray-500">Peak: {session.peak_viewers}</p>
                                )}
                              </div>
                              <div>
                                <p className="text-xs text-gray-400">Requests</p>
                                <p className="text-lg font-bold text-purple-400">{session.total_requests || 0}</p>
                                <p className="text-xs text-gray-500">
                                  {session.played_requests || 0} {isKaraokeSession ? 'performed' : 'played'}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-400">Duration</p>
                                <p className="text-lg font-bold text-green-400">{session.duration_minutes || 0}m</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-400">Earnings</p>
                                <p className="text-lg font-bold text-yellow-400">{format(session.total_earnings || 0)}</p>
                                {totalTips > 0 && (
                                  <p className="text-xs text-gray-500">+{format(totalTips)} tips</p>
                                )}
                              </div>
                            </div>

                            <div className="mt-3 pt-3 border-t border-gray-600">
                              <p className="text-xs text-gray-500 mb-2 font-semibold">Funnel (stored for this session)</p>
                              {String(session.session_id || '').startsWith('karaoke_') ? (
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 text-xs">
                                  <div>
                                    <p className="text-gray-500">Views</p>
                                    <p className="text-white font-semibold">{session.total_views ?? 0}</p>
                                  </div>
                                  <div>
                                    <p className="text-gray-500">Unique visitors</p>
                                    <p className="text-white font-semibold">{session.unique_visitors ?? 0}</p>
                                  </div>
                                  <div>
                                    <p className="text-gray-500">Free song req.</p>
                                    <p className="text-slate-200 font-semibold">
                                      {session.karaoke_free_requests != null
                                        ? session.karaoke_free_requests
                                        : Math.max(
                                            0,
                                            (session.total_requests || 0) -
                                              (session.karaoke_paid_checkouts ?? session.checkouts ?? 0)
                                          )}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-gray-500">Paid → basket</p>
                                    <p className="text-yellow-400 font-semibold">
                                      {session.karaoke_paid_cart ?? session.songs_added_to_cart ?? 0}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-gray-500">Paid checkout</p>
                                    <p className="text-green-400 font-semibold">
                                      {session.karaoke_paid_checkouts ?? session.checkouts ?? 0}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-gray-500">Conversion</p>
                                    <p className="text-cyan-300 font-semibold">
                                      {session.conversion_rate != null && session.conversion_rate !== ''
                                        ? `${parseFloat(session.conversion_rate).toFixed(1)}%`
                                        : '0%'}
                                    </p>
                                  </div>
                                </div>
                              ) : (
                                <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-xs">
                                  <div>
                                    <p className="text-gray-500">Unique visitors</p>
                                    <p className="text-white font-semibold">{session.unique_visitors ?? 0}</p>
                                  </div>
                                  <div>
                                    <p className="text-gray-500">Total views</p>
                                    <p className="text-white font-semibold">{session.total_views ?? 0}</p>
                                  </div>
                                  <div>
                                    <p className="text-gray-500">Added to cart</p>
                                    <p className="text-yellow-400 font-semibold">{session.songs_added_to_cart ?? 0}</p>
                                  </div>
                                  <div>
                                    <p className="text-gray-500">Checkouts</p>
                                    <p className="text-green-400 font-semibold">{session.checkouts ?? 0}</p>
                                  </div>
                                  <div>
                                    <p className="text-gray-500">Tips</p>
                                    <p className="text-rose-300 font-semibold">{format(totalTips)}</p>
                                  </div>
                                  <div>
                                    <p className="text-gray-500">Conversion</p>
                                    <p className="text-blue-400 font-semibold">
                                      {session.conversion_rate != null && session.conversion_rate !== ''
                                        ? `${parseFloat(session.conversion_rate).toFixed(1)}%`
                                        : '0%'}
                                    </p>
                                  </div>
                                </div>
                              )}
                            </div>
                            
                            {session.venue && (
                              <div className="mt-2">
                                <Badge variant="outline" className="border-gray-500 text-gray-300 text-xs">
                                  {session.venue}
                                </Badge>
                              </div>
                            )}
                          </div>
                          <div className="ml-3 shrink-0">
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-red-600 text-red-400 hover:bg-red-900/40"
                              onClick={() => handleDeleteSession(session.id)}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Live Status Section */}
          {liveStats && (
            <div className="pt-4 border-t border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4">Activity summary</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-700 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Radio className={`w-5 h-5 ${liveStats.isCurrentlyLive ? 'text-green-400' : 'text-gray-500'}`} />
                    <label className="text-sm font-medium text-gray-300">Current Status</label>
                  </div>
                  <p className="text-2xl font-bold text-white">
                    {liveStats.isCurrentlyLive ? (
                      <span className="text-green-400">Currently Live</span>
                    ) : (
                      <span className="text-gray-400">Offline</span>
                    )}
                  </p>
                </div>

                <div className="bg-gray-700 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <History className="w-5 h-5 text-gray-400" />
                    <label className="text-sm font-medium text-gray-300">Total Sessions</label>
                  </div>
                  <p className="text-2xl font-bold text-white">{liveStats.totalSessions || 0}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {liveStats.totalSessions > 0 ? `${liveStats.totalSessions} live session${liveStats.totalSessions > 1 ? 's' : ''} recorded` : 'Never gone live'}
                  </p>
                </div>

                {liveStats.lastLiveDate && (
                  <div className="bg-gray-700 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Calendar className="w-5 h-5 text-gray-400" />
                      <label className="text-sm font-medium text-gray-300">Last Live Date</label>
                    </div>
                    <p className="text-lg font-semibold text-white">
                      {formatUkDate(liveStats.lastLiveDate)}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {formatUkTime(liveStats.lastLiveDate)}
                    </p>
                  </div>
                )}

                <div className="bg-gray-700 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <TrendingUp className="w-5 h-5 text-gray-400" />
                    <label className="text-sm font-medium text-gray-300">Activity Status</label>
                  </div>
                  <p className="text-lg font-semibold text-white">
                    {liveStats.hasEverGoneLive ? (
                      <span className="text-green-400">Active Artist</span>
                    ) : (
                      <span className="text-orange-400">Inactive</span>
                    )}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {liveStats.hasEverGoneLive ? 'Has performed live before' : 'Never gone live'}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="pt-4 border-t border-gray-700">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-400">Created:</span>
                <span className="text-white ml-2">{formatUkDateTime(artist.created_at)}</span>
              </div>
              <div>
                <span className="text-gray-400">Last Updated:</span>
                <span className="text-white ml-2">{artist.updated_at ? formatUkDateTime(artist.updated_at) : 'Never'}</span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};
